import React, { useState, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const ClinicianFaqsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Clinician FAQs';
  }, []);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  const categoryDropdownRef = useRef(null);

  // Clinician-specific FAQ data
  const faqData = [
    {
      id: 1,
      question: 'How do I change my password?',
      answer: 'You can change your password by going to your account settings and selecting the password change option in the clinician portal.',
      category: 'Account'
    },
    {
      id: 2,
      question: 'How do I access patient files?',
      answer: 'Patient files can be accessed through the Patient Files section in the main navigation. Use the search function to find specific patients.',
      category: 'Patient Care'
    },
    {
      id: 3,
      question: 'How do I send my invoice?',
      answer: 'Invoices can be generated and sent through the Billing section in your clinician portal. Select the patients and services to create invoices.',
      category: 'Billing'
    },
    {
      id: 4,
      question: 'How do I schedule appointments?',
      answer: 'You can manage your schedule through the Calendar section. Set your availability and manage patient appointments.',
      category: 'Scheduling'
    },
    {
      id: 5,
      question: 'How do I update patient records?',
      answer: 'Patient records can be updated through the Patient Files section. Select the patient and edit their medical information.',
      category: 'Patient Care'
    },
    {
      id: 6,
      question: 'How do I access clinical telehealth consultations?',
      answer: 'Clinical telehealth consultations can be accessed through the appointment dashboard. Click "Go to Booking" for scheduled clinical appointments.',
      category: 'Telehealth'
    },
    {
      id: 7,
      question: 'How do I view my patient list?',
      answer: 'Your patient list is available in the Patients section of the main navigation where you can view all assigned patients.',
      category: 'Patient Care'
    },
    {
      id: 8,
      question: 'How do I generate medical reports?',
      answer: 'Medical reports can be generated through the Patient Files section under the Analytics tab for each patient.',
      category: 'Reports'
    },
    {
      id: 9,
      question: 'How do I manage my availability?',
      answer: 'Your availability can be managed through the Availability section where you can set working hours and time slots.',
      category: 'Scheduling'
    },
    {
      id: 10,
      question: 'How do I contact technical support?',
      answer: 'Technical support can be reached through the Help section or by contacting the IT department directly.',
      category: 'Support'
    }
  ];

  const handleViewAll = () => {
    // In a real app, this would navigate to a full FAQ page
    console.log('Viewing all FAQs');
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-2 px-6 pb-6">
        {/* First Block - Filter Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex gap-6">
            {/* Filter Category - 30% */}
            <div className="w-[30%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Category:
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                    {selectedCategories.length === 0 ? (
                      <span className="text-gray-400 text-sm">Please Select</span>
                    ) : (
                      selectedCategories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {category}
                          <button
                            onClick={() => handleCategoryRemove(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    onClick={handleAddCategory}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Category Dropdown */}
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {['Account', 'Patient Care', 'Billing', 'Scheduling', 'Telehealth', 'Reports', 'Support'].map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        disabled={selectedCategories.includes(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* I want to - 50% */}
            <div className="w-[50%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                I want to:
              </label>
              <input
                type="text"
                placeholder="I want to..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Second Block - Clinician FAQs Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-blue-600 mb-2">Clinician FAQs</h2>
          <div className="border-b-2 border-blue-600 mb-4"></div>
          <div className="space-y-4">
            {faqData.slice(0, 3).map((faq) => (
              <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                <p className="text-gray-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={handleViewAll}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition-colors"
            >
              View All
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>
    </div>
  );
};

export default ClinicianFaqsPage;

